import React from 'react';
import { TableRow, TableCell, IconButton, Tooltip } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { getDataCellClass } from 'components/tables/common/simpleTableClasses';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import ChildRowDataProcessor from './ChildRowDataProcessor';
import { AuditTableRowProps } from '../../types';
import { formatCurrency } from 'utils';
import {
  ConditionalColumns,
  getStoreConfigurationFlags,
  isLottoSummaryAllZero,
  isPosSummaryAllZero,
  prepareScratcherCountsDetails,
  prepareScratcherSalesDetails,
  prepareLottoSalesDetails,
  preparePayoutDetails,
  TotalDiscrepancyDisplay
} from '../../utils';
import hourGlassIcon from "assets/images/hourGlass.svg"
import warningIcon from "assets/images/warningIcon.svg"
import { LOTTO_TOOLTIP_MESSAGES } from 'constants/tooltipMessages';
import InventoryTicketMetric from './InventoryTicketMetric';
import BackstockStatusMetric from './BackstockStatusMetric';
import { AUDIT_TABLE_COLUMN_STYLES } from './auditTableColumnStyles';

const VARIANCE_COLUMN_STYLE: React.CSSProperties = {
  textAlign: 'right',
};
const DATE_COLUMN_STYLE: React.CSSProperties = AUDIT_TABLE_COLUMN_STYLES.date;
const renderInProgressPlaceholder = () => (
  <span className="audit-table__wip-value">-</span>
);

const AuditTableRow: React.FC<AuditTableRowProps> = ({
  row,
  rowIdx,
  isExpanded,
  isExpandable,
  onExpandClick,
  auditSummariesLength,
  borderColumns,
  storeId,
  onAuditDetails,
  selectedStore,
  effectiveDate,
  conditionalBorderColumns,
  reconciliationColumnIndex,
  showNetVariance = true,
  isLoadingAudits,
  hideStockColumns = false,
  showBackstockAccounting = false,
  backstockLedgerRow,
  backstockColumnOffset = 0,
  onAuditPackMetricClick,
  onBackstockPackMetricClick
}) => {
  // Extract conditional logic to top level for better readability
  const {
    isBothConnected,
    isBothNotConnected,
    isLottoConnectedOnly,
    isPOSConnectedOnly,
    isLottoAuthorized,
    hasPosType
  } = getStoreConfigurationFlags(selectedStore);

  // Get component values directly from response data (already transformed in auditService)
  const posScratcherQuantity = Number(row?.pos_summary?.scratcher_quantity_sold ?? 0);
  const totalSold = Number(row?.audit_summary?.total_sold ?? 0);
  const posScratcherSales = Number(row?.pos_summary?.scratcher_sales ?? 0);
  const revenue = Number(row?.audit_summary?.revenue ?? 0);
  const posLottoSales = Number(row?.pos_summary?.lotto_sales ?? 0);
  const lottoSales = Number(row?.lotto_summary?.sales ?? 0);
  const lottoPayoutsTotal = Number(row?.lotto_summary?.payouts_total ?? 0);
  const freeTicketsAmount = Number(row?.lotto_summary?.scratcher_free_tickets_paid_amount ?? 0);
  const posPayout = Number(row?.pos_summary?.payout ?? 0);

  // Get variance values directly from backend response
  const scratcherQuantityVariance = row?.reconciliation_variance?.scratcher?.quantity_variance;
  const scratcherRevenueVariance = row?.reconciliation_variance?.scratcher?.revenue_variance;
  const lottoSalesVariance = row?.reconciliation_variance?.lotto_sales_variance;
  const payoutVariance = row?.reconciliation_variance?.payout_variance;
  const totalVariance = row?.reconciliation_variance?.total_variance;

  // Check zero states using component values directly
  const isLottoZero = isLottoSummaryAllZero(row?.lotto_summary);
  const isPosZero = isPosSummaryAllZero(row?.pos_summary);

  // Prepare discrepancy details using backend data directly
  const scratcherCountsDetails = prepareScratcherCountsDetails(
    posScratcherQuantity,
    totalSold,
    scratcherQuantityVariance,
    isPosZero,
    isLottoZero,
    hasPosType
  );

  const scratcherSalesDetails = prepareScratcherSalesDetails(
    posScratcherSales,
    revenue,
    scratcherRevenueVariance,
    isPosZero,
    isLottoZero,
    hasPosType
  );

  const lottoSalesDetails = prepareLottoSalesDetails(
    posLottoSales,
    lottoSales,
    lottoSalesVariance,
    isPosZero,
    isLottoZero,
    hasPosType
  );

  const payoutDetails = preparePayoutDetails(
    lottoPayoutsTotal,
    freeTicketsAmount,
    posPayout,
    payoutVariance,
    isPosZero,
    isLottoZero,
    hasPosType
  );

  // Use total_variance directly from backend response
  const totalDiscrepancy = totalVariance !== undefined && totalVariance !== null
    ? totalVariance
    : 0;
  const wipScratcherCountValue = hasPosType ? posScratcherQuantity : totalSold;
  const wipScratcherSalesValue = hasPosType ? posScratcherSales : revenue;
  const wipPayoutValue = hasPosType ? posPayout : (lottoPayoutsTotal + freeTicketsAmount);

  const navigate = useNavigate();

  // Check if effectiveDate is greater than report_date to show warning
  const shouldShowWarning = effectiveDate && row?.report_date &&
    moment(effectiveDate).isAfter(moment(row.report_date));
  const isTodayRow = Boolean(row?.report_date) && moment(row.report_date).isSame(moment(), 'day');
  const stockStartColumnIndex = showBackstockAccounting ? 8 : 1;
  const backstockColumnIndex = hideStockColumns ? 1 : 4;
  const closingStockColumnIndex = stockStartColumnIndex + (showBackstockAccounting ? 5 : 2);
  const counterOpeningTickets = showBackstockAccounting ? backstockLedgerRow?.counter_opening_tickets : row?.audit_summary?.opening_count;
  const counterOpeningPacks = showBackstockAccounting ? backstockLedgerRow?.counter_opening_packs : row?.audit_summary?.opening_pack_count;
  const counterActivatedTickets = showBackstockAccounting ? backstockLedgerRow?.counter_activated_tickets : row?.audit_summary?.newly_activated_count;
  const counterActivatedPacks = showBackstockAccounting ? backstockLedgerRow?.counter_activated_packs : row?.audit_summary?.newly_activated_pack_count;
  const counterClosingTickets = showBackstockAccounting ? backstockLedgerRow?.counter_closing_tickets : row?.audit_summary?.closing_count;
  const counterClosingPacks = showBackstockAccounting ? backstockLedgerRow?.counter_closing_packs : row?.audit_summary?.closing_pack_count;
  const counterSoldOutTickets = backstockLedgerRow?.counter_sold_out_tickets ?? 0;
  const counterSoldOutPacks = backstockLedgerRow?.counter_sold_out_packs ?? row?.audit_summary?.counter_sold_out_pack_count ?? 0;
  const counterExpectedSalesTickets = showBackstockAccounting
    ? Number(backstockLedgerRow?.counter_expected_sales_tickets ?? 0)
    : totalSold;
  const renderStockColumns = () => {
    if (hideStockColumns) {
      return null;
    }

    return (
      <>
        <TableCell
          className={getDataCellClass(stockStartColumnIndex, rowIdx, auditSummariesLength, conditionalBorderColumns)}
          style={VARIANCE_COLUMN_STYLE}
        >
          {isTodayRow ? renderInProgressPlaceholder() : (
            <InventoryTicketMetric
              tickets={counterOpeningTickets}
              packs={counterOpeningPacks}
              emphasized={false}
              onPackClick={onAuditPackMetricClick ? (event) => onAuditPackMetricClick(event, row, 'opening') : undefined}
              packTitle="Open these opening packs in current inventory"
              zeroPlaceholder={showBackstockAccounting ? '-' : undefined}
            />
          )}
        </TableCell>
        <TableCell
          className={getDataCellClass(stockStartColumnIndex + 1, rowIdx, auditSummariesLength, conditionalBorderColumns)}
          style={VARIANCE_COLUMN_STYLE}
        >
          {isTodayRow ? renderInProgressPlaceholder() : (
            <InventoryTicketMetric
              tickets={counterActivatedTickets}
              packs={counterActivatedPacks}
              emphasized={false}
              onPackClick={onAuditPackMetricClick ? (event) => onAuditPackMetricClick(event, row, 'activated') : undefined}
              packTitle="Open these activated packs in current inventory"
              zeroPlaceholder={showBackstockAccounting ? '-' : undefined}
            />
          )}
        </TableCell>
        {showBackstockAccounting && (
          <>
            <TableCell
              className={getDataCellClass(stockStartColumnIndex + 2, rowIdx, auditSummariesLength, conditionalBorderColumns)}
              style={VARIANCE_COLUMN_STYLE}
            >
              {isTodayRow ? renderInProgressPlaceholder() : (
                <InventoryTicketMetric
                  tickets={backstockToCounterTickets}
                  packs={backstockToCounterPacks}
                  emphasized={false}
                  onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'returned_to_counter') : undefined}
                  packTitle="Open these backstock-to-counter packs in current inventory"
                  zeroPlaceholder="-"
                />
              )}
            </TableCell>
            <TableCell
              className={getDataCellClass(stockStartColumnIndex + 3, rowIdx, auditSummariesLength, conditionalBorderColumns)}
              style={VARIANCE_COLUMN_STYLE}
            >
              {isTodayRow ? renderInProgressPlaceholder() : (
                <InventoryTicketMetric
                  tickets={backstockFromCounterTickets}
                  packs={backstockFromCounterPacks}
                  emphasized={false}
                  onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'moved_from_counter') : undefined}
                  packTitle="Open these counter-to-backstock packs in current inventory"
                  zeroPlaceholder="-"
                />
              )}
            </TableCell>
          </>
        )}
        {showBackstockAccounting && (
          <TableCell
            className={getDataCellClass(stockStartColumnIndex + 4, rowIdx, auditSummariesLength, conditionalBorderColumns)}
            style={VARIANCE_COLUMN_STYLE}
          >
            {isTodayRow ? renderInProgressPlaceholder() : (
              <InventoryTicketMetric
                tickets={counterSoldOutTickets}
                packs={counterSoldOutPacks}
                emphasized={false}
                onPackClick={onAuditPackMetricClick ? (event) => onAuditPackMetricClick(event, row, 'counter_sold_out') : undefined}
                packTitle="Open these counter sold-out packs in current inventory"
                zeroPlaceholder="-"
                zeroTicketPlaceholder="-"
              />
            )}
          </TableCell>
        )}
        <TableCell
          className={getDataCellClass(closingStockColumnIndex, rowIdx, auditSummariesLength, conditionalBorderColumns)}
          style={VARIANCE_COLUMN_STYLE}
        >
          {isTodayRow ? renderInProgressPlaceholder() : (
            <InventoryTicketMetric
              tickets={counterClosingTickets}
              packs={counterClosingPacks}
              emphasized={false}
              onPackClick={onAuditPackMetricClick ? (event) => onAuditPackMetricClick(event, row, 'closing') : undefined}
              packTitle="Open these closing packs in current inventory"
              zeroPlaceholder={showBackstockAccounting ? '-' : undefined}
            />
          )}
        </TableCell>
      </>
    );
  };

  const handleDateClick = () => {
    // Navigate to audits page with auditDate as query parameter
    navigate(`/store/${storeId}/audits?auditDate=${moment(row?.report_date).format('YYYY-MM-DD')}`);
  };

  const handleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isExpandable) {
      return;
    }

    onExpandClick(rowIdx);
  };
  const auditCount = Number(row?.audit_summary?.audit_count ?? row?.audits?.length ?? 0);
  const backstockFromCounterPacks = Number(backstockLedgerRow?.moved_from_counter_packs ?? 0);
  const backstockFromCounterTickets = Number(backstockLedgerRow?.moved_from_counter_tickets ?? 0);
  const backstockActivatedPacks = Number(backstockLedgerRow?.activated_to_backstock_packs ?? 0);
  const backstockActivatedTickets = Number(backstockLedgerRow?.activated_to_backstock_tickets ?? 0);
  const backstockToCounterPacks = Number(backstockLedgerRow?.returned_to_counter_packs ?? 0);
  const backstockToCounterTickets = Number(backstockLedgerRow?.returned_to_counter_tickets ?? 0);
  const backstockReturnedToLotteryPacks = Number(backstockLedgerRow?.returned_to_lottery_packs ?? 0);
  const backstockReturnedToLotteryTickets = Number(backstockLedgerRow?.returned_to_lottery_tickets ?? 0);
  const backstockInventoryDepletionPacks = Number(backstockLedgerRow?.inventory_depletion_packs ?? 0);
  const backstockInventoryDepletionTickets = Number(backstockLedgerRow?.inventory_depletion_tickets ?? 0);
  const statusItems: Array<{
    key: string;
    title: string;
    icon: string;
    label: string;
  }> = [];

  if (row?.audit_missing) {
    statusItems.push({
      key: 'audit',
      title: LOTTO_TOOLTIP_MESSAGES.AUDIT_MISSING,
      icon: hourGlassIcon,
      label: 'Audit',
    });
  }

  if (isLottoAuthorized && isLottoZero) {
    statusItems.push({
      key: 'lotto',
      title: shouldShowWarning
        ? LOTTO_TOOLTIP_MESSAGES.LOTTO_DATA_NOT_AVAILABLE
        : LOTTO_TOOLTIP_MESSAGES.DATA_PENDING,
      icon: shouldShowWarning ? warningIcon : hourGlassIcon,
      label: 'Lotto',
    });
  }

  if (hasPosType && isPosZero) {
    statusItems.push({
      key: 'pos',
      title: shouldShowWarning
        ? LOTTO_TOOLTIP_MESSAGES.POS_DATA_NOT_AVAILABLE
        : LOTTO_TOOLTIP_MESSAGES.POS_DATA_PENDING,
      icon: shouldShowWarning ? warningIcon : hourGlassIcon,
      label: 'POS',
    });
  }


  return (
    <React.Fragment>
      <TableRow
        className={[
          rowIdx % 2 === 0 ? "audit-table__row--even" : "audit-table__row--odd",
          isExpanded ? "audit-table__row--expanded-parent" : "",
          "cursor-pointer",
        ].filter(Boolean).join(" ")}
        onClick={handleDateClick}
      >
        <TableCell
          className={getDataCellClass(0, rowIdx, auditSummariesLength, conditionalBorderColumns)}
          style={DATE_COLUMN_STYLE}
        >
          <div className="audit-table__date-cell-content">
            <div className="audit-table__date-main">
              <IconButton
                size="small"
                onClick={handleExpand}
                className="audit-table__expand-button"
                disabled={!isExpandable}
                aria-label={isExpanded ? 'Collapse audit details' : 'Expand audit details'}
                sx={{
                  padding: '0 !important',
                  marginLeft: '-4px !important',
                  marginRight: '0 !important',
                  minWidth: '18px !important',
                  width: '18px !important',
                  height: '18px !important',
                  visibility: isExpandable ? 'visible' : 'hidden',
                }}
              >
                {isExpanded ? (
                  <KeyboardArrowDown sx={{ fontSize: 16 }} />
                ) : (
                  <KeyboardArrowRight sx={{ fontSize: 16 }} />
                )}
              </IconButton>
              <div className="audit-table__date-text-block">
                <p
                  className="audit-table-date audit-table__date-line"
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/store/${storeId}/audits?auditDate=${moment(row?.report_date).format('YYYY-MM-DD')}`);
                  }}
                >
                  <span className="audit-table__date-value">{moment(row?.report_date).format('ddd, MM/DD/YYYY')}</span>
                  {auditCount > 0 && (
                    <span
                      className="audit-table__audit-count-badge"
                    >
                      {auditCount}
                    </span>
                  )}
                </p>
                {isTodayRow && (
                  <div className="audit-table__day-progress-badge">
                    <AccessTimeOutlinedIcon sx={{ fontSize: 11, color: 'inherit' }} />
                    <span>DAY IN PROGRESS</span>
                  </div>
                )}
              </div>
            </div>

            {/* Conditionally render status section based on store configuration and missing-audit flag */}
            {!isTodayRow && statusItems.length > 0 && (
              <div className="audit-table__status-group">
                {statusItems.map((item, index) => (
                  <React.Fragment key={item.key}>
                    {index > 0 && <div className="divider-1px" />}
                    <Tooltip title={item.title}>
                      <div className="audit-table__status-item">
                        <img src={item.icon} alt="info" />
                        <p className="store_cell_status audit-table__status-label">{item.label}</p>
                      </div>
                    </Tooltip>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </TableCell>

        {!showBackstockAccounting && renderStockColumns()}

        {!showBackstockAccounting && (
          <TableCell
            className={getDataCellClass(backstockColumnIndex, rowIdx, auditSummariesLength, conditionalBorderColumns)}
            style={VARIANCE_COLUMN_STYLE}
          >
            {isTodayRow ? renderInProgressPlaceholder() : (
              <BackstockStatusMetric
                tickets={backstockLedgerRow?.closing_tickets ?? 0}
                packs={backstockLedgerRow?.closing_packs ?? 0}
                onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'closing') : undefined}
                packTitle="Activated or settled packs LottoReco still believes are in backstock and outside audited inventory"
              />
            )}
          </TableCell>
        )}

        {showBackstockAccounting && (
          <>
            <TableCell
              className={getDataCellClass(1, rowIdx, auditSummariesLength, conditionalBorderColumns)}
              style={VARIANCE_COLUMN_STYLE}
            >
              {isTodayRow ? renderInProgressPlaceholder() : (
                <InventoryTicketMetric
                  tickets={backstockLedgerRow?.opening_tickets ?? 0}
                  packs={backstockLedgerRow?.opening_packs ?? 0}
                  emphasized={false}
                  onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'opening') : undefined}
                  packTitle="Open these backstock opening packs in current inventory"
                  zeroPlaceholder="-"
                />
              )}
            </TableCell>
            <TableCell
              className={getDataCellClass(2, rowIdx, auditSummariesLength, conditionalBorderColumns)}
              style={VARIANCE_COLUMN_STYLE}
            >
              {isTodayRow ? renderInProgressPlaceholder() : (
                <InventoryTicketMetric
                  tickets={backstockActivatedTickets}
                  packs={backstockActivatedPacks}
                  emphasized={false}
                  onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'activated_to_backstock') : undefined}
                  packTitle="Open these activation-to-backstock packs in current inventory"
                  zeroPlaceholder="-"
                />
              )}
            </TableCell>
            <TableCell
              className={getDataCellClass(3, rowIdx, auditSummariesLength, conditionalBorderColumns)}
              style={VARIANCE_COLUMN_STYLE}
            >
              {isTodayRow ? renderInProgressPlaceholder() : (
                <InventoryTicketMetric
                  tickets={backstockFromCounterTickets}
                  packs={backstockFromCounterPacks}
                  emphasized={false}
                  onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'moved_from_counter') : undefined}
                  packTitle="Open these counter-to-backstock packs in current inventory"
                  zeroPlaceholder="-"
                />
              )}
            </TableCell>
            <TableCell
              className={getDataCellClass(4, rowIdx, auditSummariesLength, conditionalBorderColumns)}
              style={VARIANCE_COLUMN_STYLE}
            >
              {isTodayRow ? renderInProgressPlaceholder() : (
                <InventoryTicketMetric
                  tickets={backstockToCounterTickets}
                  packs={backstockToCounterPacks}
                  emphasized={false}
                  onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'returned_to_counter') : undefined}
                  packTitle="Open these backstock-to-counter packs in current inventory"
                  zeroPlaceholder="-"
                />
              )}
            </TableCell>
            <TableCell
              className={getDataCellClass(5, rowIdx, auditSummariesLength, conditionalBorderColumns)}
              style={VARIANCE_COLUMN_STYLE}
            >
              {isTodayRow ? renderInProgressPlaceholder() : (
                <InventoryTicketMetric
                  tickets={backstockReturnedToLotteryTickets}
                  packs={backstockReturnedToLotteryPacks}
                  emphasized={false}
                  onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'returned_to_lottery') : undefined}
                  packTitle="Open these returned-to-lottery packs in current inventory"
                  zeroPlaceholder="-"
                />
              )}
            </TableCell>
            <TableCell
              className={getDataCellClass(6, rowIdx, auditSummariesLength, conditionalBorderColumns)}
              style={VARIANCE_COLUMN_STYLE}
            >
              {isTodayRow ? renderInProgressPlaceholder() : (
                <InventoryTicketMetric
                  tickets={backstockInventoryDepletionTickets}
                  packs={backstockInventoryDepletionPacks}
                  emphasized={false}
                  onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'inventory_depletion') : undefined}
                  packTitle="Open these sold-out backstock packs in current inventory"
                  zeroPlaceholder="-"
                />
              )}
            </TableCell>
            <TableCell
              className={getDataCellClass(7, rowIdx, auditSummariesLength, conditionalBorderColumns)}
              style={VARIANCE_COLUMN_STYLE}
            >
              {isTodayRow ? renderInProgressPlaceholder() : (
                <InventoryTicketMetric
                  tickets={backstockLedgerRow?.closing_tickets ?? 0}
                  packs={backstockLedgerRow?.closing_packs ?? 0}
                  emphasized={false}
                  onPackClick={onBackstockPackMetricClick ? (event) => onBackstockPackMetricClick(event, row, 'closing') : undefined}
                  packTitle="Open these backstock closing packs in current inventory"
                  zeroPlaceholder="-"
                />
              )}
            </TableCell>
          </>
        )}

        {showBackstockAccounting && renderStockColumns()}

        {!hasPosType && (
          <TableCell
            className={getDataCellClass(5 + backstockColumnOffset, rowIdx, auditSummariesLength, conditionalBorderColumns)}
            style={VARIANCE_COLUMN_STYLE}
          >
            {isTodayRow ? renderInProgressPlaceholder() : counterExpectedSalesTickets}
          </TableCell>
        )}

        {!hasPosType && (
          <TableCell
            className={getDataCellClass(6 + backstockColumnOffset, rowIdx, auditSummariesLength, conditionalBorderColumns)}
            style={VARIANCE_COLUMN_STYLE}
          >
            {isTodayRow ? renderInProgressPlaceholder() : formatCurrency(revenue)}
          </TableCell>
        )}


        <ConditionalColumns
          isBothConnected={isBothConnected}
          isBothNotConnected={isBothNotConnected}
          isLottoConnectedOnly={isLottoConnectedOnly}
          isPOSConnectedOnly={isPOSConnectedOnly}
          scratcherCountsDetails={scratcherCountsDetails}
          lottoSalesDetails={lottoSalesDetails}
          scratcherSalesDetails={scratcherSalesDetails}
          payoutDetails={payoutDetails}
          totalDiscrepancy={totalDiscrepancy}
          borderColumns={conditionalBorderColumns}
          lottoPayoutsTotal={lottoPayoutsTotal}
          freeTicketPayoutsTotal={freeTicketsAmount}
          posPayout={posPayout}
          lottoSales={lottoSales}
          posLottoSales={posLottoSales}
          lottoSalesVariance={lottoSalesVariance}
          scratcherQuantityVariance={scratcherQuantityVariance}
          scratcherRevenueVariance={scratcherRevenueVariance}
          payoutVariance={payoutVariance}
          hasPosType={hasPosType}
          isInProgressRow={isTodayRow}
          scratcherCountDisplayValue={wipScratcherCountValue}
          scratcherSalesDisplayValue={wipScratcherSalesValue}
          expectedSalesDisplayValue={revenue}
          expectedQuantityDisplayValue={counterExpectedSalesTickets}
          posScratcherQuantityDisplayValue={posScratcherQuantity}
          payoutDisplayValue={wipPayoutValue}
          rowIdx={rowIdx}
          totalRows={auditSummariesLength}
          columnOffset={backstockColumnOffset}
          hideLottoAndPayout={showBackstockAccounting}
        />


        {showNetVariance && (
          <TableCell
            className={getDataCellClass(reconciliationColumnIndex, rowIdx, auditSummariesLength, conditionalBorderColumns)}
            style={VARIANCE_COLUMN_STYLE}
          >
            {isTodayRow ? (
              renderInProgressPlaceholder()
            ) : (
              <TotalDiscrepancyDisplay totalDiscrepancy={totalDiscrepancy} />
            )}
          </TableCell>
        )}
      </TableRow>

      {isExpanded && (
        <ChildRowDataProcessor
          audits={row.audits}
          isLoadingAudits={isLoadingAudits}
          borderColumns={conditionalBorderColumns}
          onAuditDetails={onAuditDetails}
          selectedStore={selectedStore}
          reportDate={row.report_date}
          isBothConnected={isBothConnected}
          isBothNotConnected={isBothNotConnected}
          isLottoConnectedOnly={isLottoConnectedOnly}
          isPOSConnectedOnly={isPOSConnectedOnly}
          reconciliationColumnIndex={reconciliationColumnIndex}
          showNetVariance={showNetVariance}
          hideStockColumns={hideStockColumns}
          backstockColumnOffset={backstockColumnOffset}
        />
      )}
    </React.Fragment>
  );
};

export default AuditTableRow; 
